<template>
  <div class="connections-graph-view">
    <div class="graph-header">
      <div class="header-left">
        <div class="wool-line-id">
          <span class="wool-line-icon"></span>
          <span class="line-id-text">{{ getCurrentLineId() }}</span>
        </div>
        <h3>Supply Chain Connections</h3>
      </div>
      <p class="text-muted">Click on any node to view detailed information</p>
    </div>
    <div class="graph-container">
      <svg id="connections-svg" width="100%" height="1200"></svg>
    </div>

    <!-- Node Details Modal - No background overlay -->
    <template v-if="showNodeDetails && selectedNode">
      <div 
        class="node-details-modal" 
        @click="closeNodeDetails"
      >
      <div 
        class="node-details-card" 
        @click.stop
        :class="{ 'right-panel': selectedNode.type === 'shearing' }"
        :style="selectedNode.type === 'shearing' ? {} : { 
          left: modalPosition.x + 'px',
          top: modalPosition.y + 'px'
        }"
      >
        <!-- Shearing-specific detailed modal -->
        <div v-if="selectedNode.type === 'shearing'" class="shearing-modal">
          <div class="section-header">
            <h3>Shearing & Processing</h3>
          </div>
          
          <div class="progress-section">
            <div class="progress-item">
              <span class="progress-label">Risks</span>
              <div class="progress-bar">
                <div class="progress-fill green" style="width: 85%"></div>
              </div>
              <span class="progress-value">Low</span>
            </div>
            <div class="progress-item">
              <span class="progress-label">Compliance</span>
              <div class="progress-bar">
                <div class="progress-fill green" style="width: 95%"></div>
              </div>
              <span class="progress-value">High</span>
            </div>
          </div>

          <div class="detail-section">
            <h4>Shearing operations</h4>
            <div class="company-info">
              <strong>{{ selectedNode.properties.shearingCompany || 'Southern Shearers Pty Ltd' }}</strong>
              <div class="company-details">
                <div><strong>Team Lead:</strong> {{ selectedNode.properties.teamLead || 'Riley Morgan' }}</div>
                <div><strong>Sheep Shorn:</strong> {{ selectedNode.properties.sheepCount || '343 animals' }}</div>
                <div><strong>Shed:</strong> {{ selectedNode.properties.shed || 'Main Shed' }}</div>
              </div>
              <div class="status-badge green">Status: SustainaWool</div>
            </div>
          </div>

          <div class="detail-section">
            <h4>Wool Classing</h4>
            <div class="classer-info">
              <div class="classer-name">Classer: <span class="orange">{{ selectedNode.properties.classerName || 'Taylor Quinn' }}</span></div>
              <div class="classer-id">({{ selectedNode.properties.classerId || 'AWEX990012' }})</div>
              <div class="classer-details">
                <div><strong>Description:</strong> {{ selectedNode.properties.description || 'Merino fleece' }}</div>
                <div><strong>Line ID:</strong> {{ selectedNode.properties.lineId || getCurrentLineId() }}</div>
                <div><strong>Bales Produced:</strong> {{ selectedNode.properties.balesProduced || '2 bales (365kg total)' }}</div>
              </div>
              <button class="view-certificate-btn">View AWEX certificate</button>
            </div>
          </div>

          <div class="detail-section">
            <h4>Quality Testing</h4>
            <div class="testing-placeholder">
              <!-- Testing details would go here -->
            </div>
          </div>
        </div>

        <!-- Improved modal for all other node types -->
        <div v-else>
          <div class="card-header">
            <h3>{{ selectedNode.label || selectedNode.name }}</h3>
          </div>
          <div class="card-body">
            <div v-for="(value, key) in filteredNodeProperties" :key="key" class="detail-item">
              <strong>{{ formatPropertyName(key) }}:</strong> {{ value || '-' }}
            </div>
            <div class="card-source">
              Source: {{ getNodeSource(selectedNode.type) }}
            </div>
          </div>
          <div class="card-footer">
            <button class="details-btn" @click="closeNodeDetails">Close</button>
          </div>
        </div>
      </div>
    </div>
    </template>
  </div>
</template>

<script>
import * as d3 from 'd3';

export default {
  props: {
    searchResult: {
      type: Object,
      default: () => ({})
    },
    selectedItem: {
      type: Object,
      default: () => ({})
    },
    selectedCard: {
      type: [Object, null],
      default: null
    },
    selectedResultIndex: {
      type: Number,
      default: 0
    }
  },
  
  data() {
    return {
      nodes: [],
      links: [],
      selectedNode: null,
      svg: null,
      simulation: null,
      showNodeDetails: false,
      modalPosition: { x: 0, y: 0 },
      generateTimeout: null, // Debounce timeout for graph generation
      // Properties to exclude from farm node details
      excludedFarmProperties: [
        'ABN', 'Email', 'WaterUse', 'LandImpact', 'PesticideUse', 
        'BiodiversityPractices', 'CarbonReportingStatus', 'GHGScopesCovered', 
        'TraceabilityScore', 'Lat', 'Lon'
      ]
    };
  },
  
  computed: {
    filteredNodeProperties() {
      if (!this.selectedNode || !this.selectedNode.properties) return {};
      
      // If this is a farm node, filter out excluded properties
      if (this.selectedNode.type === 'farm') {
        const filteredProps = {};
        Object.keys(this.selectedNode.properties).forEach(key => {
          if (!this.excludedFarmProperties.includes(key)) {
            filteredProps[key] = this.selectedNode.properties[key];
          }
        });
        return filteredProps;
      }
      
      // For other node types, return all properties
      return this.selectedNode.properties;
    }
  },

  mounted() {
    this.initGraph();
    
    // If we already have search results when mounted, generate the graph
    if (this.searchResult && this.searchResult.results && this.searchResult.results.length > 0) {
      this.$nextTick(() => {
        this.debouncedGenerateGraphData();
      });
    }
  },

  watch: {
    searchResult: {
      handler() {
        this.debouncedGenerateGraphData();
      },
      deep: true
    },
    selectedItem: {
      handler() {
        this.debouncedGenerateGraphData();
      },
      deep: true
    },
    selectedResultIndex: {
      handler() {
        this.debouncedGenerateGraphData();
      }
    }
  },

  methods: {
    debouncedGenerateGraphData() {
      // Clear any existing timeout
      if (this.generateTimeout) {
        clearTimeout(this.generateTimeout);
      }
      
      // Set a new timeout to generate graph after 300ms
      // This ensures all watchers have fired before generating
      this.generateTimeout = setTimeout(() => {
        this.generateGraphData();
      }, 300);
    },

    /**
     * Fetch entity-specific data from Supabase based on entity type and identifier
     * @param {string} entityType - Type of entity (farm, brand, auction, etc.)
     * @param {string} entityId - Identifier for the entity (PIC, name, etc.)
     * @returns {Promise<Object>} - Entity data with connections
     */
    async fetchEntityData(entityType, entityId) {
      try {
        console.log(`Fetching data for ${entityType} with ID ${entityId}`);
        let entityData = null;
        let connections = [];
        
        // Handle specific entities mentioned in requirements
        const specificEntities = {
          'Sydney Wool Auction Center': { type: 'auction', id: 'Sydney Wool Auction Center' },
          'Icebreaker': { type: 'brand', id: 'Icebreaker' },
          'Uniqlo': { type: 'brand', id: 'Uniqlo' },
          'SustainaWool Integrity Scheme': { type: 'certification', id: 'SustainaWool' },
          'Australian Wool Exchange': { type: 'certification', id: 'AWEX' },
          'Australian Wool Testing Authority': { type: 'testing', id: 'AWTA' },
          'Banksia Vale': { type: 'farm', id: 'NSWBV5566' },
          'Bluegum Ridge': { type: 'farm', id: 'NSWBR9012' },
          'Eucalyptus Park': { type: 'farm', id: 'NSWEP2345' },
          'Ironbark Station': { type: 'farm', id: 'NSWIB3344' },
          'Kookaburra Plains': { type: 'farm', id: 'NSWKP1020' },
          'Redbank Hill': { type: 'farm', id: 'NSWRH2468' },
          'Rivergum Plains': { type: 'farm', id: 'NSWRP4321' },
          'Snowy View': { type: 'farm', id: 'NSWSV1357' },
          'Waratah Downs': { type: 'farm', id: 'NSWWD7788' },
          'Wattle Creek': { type: 'farm', id: 'NSWWC6789' }
        };
        
        // Check if this is one of our specific entities
        if (specificEntities[entityId]) {
          entityType = specificEntities[entityId].type;
          entityId = specificEntities[entityId].id;
        }
        
        // Determine which table to query based on entity type
        switch(entityType) {
          case 'farm':
            // Query farm_profiles table
            const { data: farmData, error: farmError } = await this.$supabase
              .from('farm_profiles')
              .select('*')
              .eq('PIC', entityId)
              .single();
              
            if (farmError) throw farmError;
            entityData = farmData;
            
            // Get farm connections from trace_index
            const { data: farmConnections, error: farmConnError } = await this.$supabase
              .from('trace_index')
              .select('LineID, TestCertID, LotNo, BrokerCode, FactoryID, ConsignmentID, Brand')
              .eq('PIC', entityId);
              
            if (farmConnError) throw farmConnError;
            connections = farmConnections;
            break;
            
          case 'brand':
            // Query brands table
            const { data: brandData, error: brandError } = await this.$supabase
              .from('brands')
              .select('*')
              .eq('name', entityId)
              .single();
              
            if (brandError) throw brandError;
            entityData = brandData;
            
            // Get brand connections from trace_index
            const { data: brandConnections, error: brandConnError } = await this.$supabase
              .from('trace_index')
              .select('PIC, LineID, TestCertID, LotNo, BrokerCode, FactoryID, ConsignmentID')
              .eq('Brand', entityId);
              
            if (brandConnError) throw brandConnError;
            connections = brandConnections;
            break;
            
          case 'auction':
            // Query auction_centers table
            const { data: auctionData, error: auctionError } = await this.$supabase
              .from('auction_centers')
              .select('*')
              .eq('name', entityId)
              .single();
              
            if (auctionError) throw auctionError;
            entityData = auctionData;
            
            // Get auction connections from trace_index and auction_catalog
            const { data: auctionConnections, error: auctionConnError } = await this.$supabase
              .from('auction_catalog')
              .select('LotNo, LineID')
              .eq('SaleCenter', entityId);
              
            if (auctionConnError) throw auctionConnError;
            
            // Get full trace data for each lot
            if (auctionConnections && auctionConnections.length > 0) {
              const lotNos = auctionConnections.map(conn => conn.LotNo);
              const { data: traceData, error: traceError } = await this.$supabase
                .from('trace_index')
                .select('PIC, LineID, TestCertID, BrokerCode, FactoryID, ConsignmentID, Brand')
                .in('LotNo', lotNos);
                
              if (traceError) throw traceError;
              connections = traceData;
            }
            break;
            
          case 'certification':
            // Query certification_bodies table
            const { data: certData, error: certError } = await this.$supabase
              .from('certification_bodies')
              .select('*')
              .eq('name', entityId)
              .single();
              
            if (certError) throw certError;
            entityData = certData;
            
            // Get certified farms
            const { data: certFarms, error: certFarmError } = await this.$supabase
              .from('sustainability_certificates')
              .select('PIC, scheme')
              .eq('scheme', entityId);
              
            if (certFarmError) throw certFarmError;
            
            // Get trace data for certified farms
            if (certFarms && certFarms.length > 0) {
              const farmPICs = certFarms.map(farm => farm.PIC);
              const { data: traceData, error: traceError } = await this.$supabase
                .from('trace_index')
                .select('PIC, LineID, TestCertID, LotNo, BrokerCode, FactoryID, ConsignmentID, Brand')
                .in('PIC', farmPICs);
                
              if (traceError) throw traceError;
              connections = traceData;
            }
            break;
            
          case 'testing':
            // Query testing_labs table
            const { data: labData, error: labError } = await this.$supabase
              .from('testing_labs')
              .select('*')
              .eq('name', entityId)
              .single();
              
            if (labError) throw labError;
            entityData = labData;
            
            // Get test certificates from this lab
            const { data: testCerts, error: testCertError } = await this.$supabase
              .from('awta_results')
              .select('TestCertID, LineID')
              .eq('lab', entityId);
              
            if (testCertError) throw testCertError;
            
            // Get trace data for tested wool lines
            if (testCerts && testCerts.length > 0) {
              const certIDs = testCerts.map(cert => cert.TestCertID);
              const { data: traceData, error: traceError } = await this.$supabase
                .from('trace_index')
                .select('PIC, LineID, LotNo, BrokerCode, FactoryID, ConsignmentID, Brand')
                .in('TestCertID', certIDs);
                
              if (traceError) throw traceError;
              connections = traceData;
            }
            break;
            
          default:
            console.warn(`Unknown entity type: ${entityType}`);
            return { entityData: null, connections: [] };
        }
        
        return { entityData, connections };
      } catch (error) {
        console.error('Error fetching entity data:', error);
        return { entityData: null, connections: [] };
      }
    },
    
    initGraph() {
      const container = document.getElementById('connections-svg');
      if (!container) {
        console.error('SVG container not found');
        return;
      }
      const width = container.clientWidth || 800;
      const height = 1200;

      // Clear any existing content
      d3.select(container).selectAll('*').remove();

      this.svg = d3.select(container)
        .attr('width', width)
        .attr('height', height);

      // Add definitions for gradients and patterns
      const defs = this.svg.append('defs');
      
      // Orange gradient for nodes
      const gradient = defs.append('linearGradient')
        .attr('id', 'orangeGradient')
        .attr('x1', '0%')
        .attr('y1', '0%')
        .attr('x2', '100%')
        .attr('y2', '100%');
      
      gradient.append('stop')
        .attr('offset', '0%')
        .attr('stop-color', '#ff7f50')
        .attr('stop-opacity', 1);
      
      gradient.append('stop')
        .attr('offset', '100%')
        .attr('stop-color', '#ff6347')
        .attr('stop-opacity', 1);

      this.generateGraphData();
    },

    async generateGraphData() {
      if (this.isGenerating) {
        console.log('DEBUG - Already generating, skipping');
        return;
      }
      this.isGenerating = true;
      
      // Clear any pending timeouts to prevent duplicate calls
      if (this.generateTimeout) {
        clearTimeout(this.generateTimeout);
        this.generateTimeout = null;
      }
      
      console.log('DEBUG - Starting generateGraphData');
      try {
        // Clear existing nodes and links
        this.nodes = [];
        this.links = [];
        
        let dataToUse = null;
        let entityType = null;
        let entityId = null;
      
        console.log('DEBUG - selectedItem:', this.selectedItem);
        console.log('DEBUG - searchResult:', this.searchResult);
      
      if (this.selectedItem && Object.keys(this.selectedItem).length > 0) {
        dataToUse = this.selectedItem;
        
        // Determine entity type and ID from the selected item
        if (dataToUse.PIC) {
          entityType = 'farm';
          entityId = dataToUse.PIC;
        } else if (dataToUse.id && typeof dataToUse.id === 'string' && dataToUse.id.startsWith('farm-')) {
          // Handle farm IDs in the format 'farm-NSWIB3344'
          entityType = 'farm';
          entityId = dataToUse.id.substring(5); // Extract PIC by removing 'farm-' prefix
          console.log('Extracted farm PIC from ID:', entityId);
        } else if (dataToUse.name && dataToUse.type === 'brand') {
          entityType = 'brand';
          entityId = dataToUse.name;
        } else if (dataToUse.name && (dataToUse.type === 'auction' || dataToUse.type === 'auction_house')) {
          entityType = 'auction';
          entityId = dataToUse.name;
          console.log('DEBUG - Handling auction/auction_house entity:', dataToUse.name);
        } else if (dataToUse.name && dataToUse.type === 'certification') {
          entityType = 'certification';
          entityId = dataToUse.name;
        } else if (dataToUse.name && dataToUse.type === 'testing') {
          entityType = 'testing';
          entityId = dataToUse.name;
        }
      } else if (this.searchResult && this.searchResult.results && this.searchResult.results.length > 0) {
        // Fall back to searchResult if selectedItem is not available
        dataToUse = this.searchResult.results[this.selectedResultIndex] || this.searchResult.results[0];
        
        // Determine entity type and ID from search result
        if (dataToUse.PIC) {
          entityType = 'farm';
          entityId = dataToUse.PIC;
        } else if (dataToUse.id && typeof dataToUse.id === 'string' && dataToUse.id.startsWith('farm-')) {
          // Handle farm IDs in the format 'farm-NSWIB3344'
          entityType = 'farm';
          entityId = dataToUse.id.substring(5); // Extract PIC by removing 'farm-' prefix
          console.log('Extracted farm PIC from search result ID:', entityId);
        } else if (dataToUse.name && dataToUse.type === 'brand') {
          entityType = 'brand';
          entityId = dataToUse.name;
        } else if (dataToUse.name && (dataToUse.type === 'auction' || dataToUse.type === 'auction_house')) {
          entityType = 'auction';
          entityId = dataToUse.name;
          console.log('DEBUG - Handling auction/auction_house entity from search result:', dataToUse.name);
        } else if (dataToUse.name && dataToUse.type === 'certification') {
          entityType = 'certification';
          entityId = dataToUse.name;
        } else if (dataToUse.name && dataToUse.type === 'testing') {
          entityType = 'testing';
          entityId = dataToUse.name;
        }
      }
      
      // If we don't have any data to use, return early
      if (!dataToUse) {
        console.warn('No data available for graph visualization');
        return;
      }
      
      // Check if we have a specific entity to fetch data for
      if (entityType && entityId) {
        console.log('DEBUG - Will fetch data for entity type:', entityType, 'with ID:', entityId);
        try {
          // Show loading state
          this.$emit('loading', true);
          
          // Fetch entity-specific data from Supabase
          const { entityData, connections } = await this.fetchEntityData(entityType, entityId);
          console.log('DEBUG - Fetched entity data:', entityData);
          console.log('DEBUG - Fetched connections:', connections);
          
          if (entityData) {
            // Merge the fetched entity data with the existing data
            dataToUse = { ...dataToUse, ...entityData, connections };
            console.log('DEBUG - Combined data to use:', dataToUse);
          } else {
            console.warn('DEBUG - No entity data returned from fetchEntityData');
          }
          
          // Hide loading state
          this.$emit('loading', false);
        } catch (error) {
          console.error('Error fetching entity data:', error);
          this.$emit('loading', false);
        }
      }

        // Create supply chain nodes for the selected result only
        this.createNodesForResult(dataToUse);
        this.createSupplyChainLinks(dataToUse);
        this.updateVisualization();
      } finally {
        this.isGenerating = false;
      }
    },

    createNodesForResult(result) {
      // Safety check - if no result, don't try to create nodes
      if (!result) {
        console.warn('No result data provided to createNodesForResult');
        return;
      }
      
      // Check if we have connections data from Supabase
      const connections = result.connections || [];
      
      const baseId = result.PIC || result.LineID || result.ConsignmentID || result.code || 'unknown';
      const container = document.getElementById('connections-svg');
      const width = container ? container.clientWidth || 800 : 800;
      const height = 1200;
      
      // Calculate center position
      const centerX = width / 2;
      
      // Create a structured hierarchical layout with proper spacing
      const positions = {};
      
      // Define the vertical spacing between layers - increased for better separation
      const verticalSpacing = 150;
      
      // Define the horizontal spacing between nodes in the same layer
      const horizontalSpacing = 180;
      
      // Define layer heights - consistent spacing from top
      const layerHeights = {
        layer0: 100,  // Root entity (farm or classer)
        layer1: 250,  // First level connections
        layer2: 400,  // Second level connections
        layer3: 550,  // Third level connections
        layer4: 700,  // Fourth level connections
        layer5: 850   // Fifth level connections
      };
      
      // Farm node at the top center as the root node
      positions.farm = { x: centerX, y: layerHeights.layer0 };
      
      // Classer node positioned in layer1 (not at the top with farm)
      // We'll use the classer_node position for the actual classer
      
      // For classer view - farms they work with in first layer
      const farmPositions = this.calculateHorizontalPositions(centerX, layerHeights.layer1, horizontalSpacing, 5);
      for (let i = 0; i < 5; i++) {
        positions[`farm-${i}`] = farmPositions[i];
      }
      
      // For farm view - shearing and classer in first layer
      positions.shearing = { x: centerX - horizontalSpacing/2, y: layerHeights.layer1 };
      positions.classer_node = { x: centerX + horizontalSpacing/2, y: layerHeights.layer1 };
      
      // Layer 2: Wool lines, NWD declarations
      const woolLinePositions = this.calculateHorizontalPositions(centerX, layerHeights.layer2, horizontalSpacing, 5);
      for (let i = 0; i < 5; i++) {
        positions[`wool-line-${i}`] = woolLinePositions[i];
      }
      positions.nwd = { x: centerX - horizontalSpacing, y: layerHeights.layer2 };
      positions['wool-line'] = positions.line = { x: centerX, y: layerHeights.layer2 };
      positions.mob = { x: centerX + horizontalSpacing, y: layerHeights.layer2 };
      
      // Layer 3: Testing and Auction
      const testingPositions = this.calculateHorizontalPositions(centerX - horizontalSpacing, layerHeights.layer3, horizontalSpacing/2, 3);
      const auctionPositions = this.calculateHorizontalPositions(centerX + horizontalSpacing, layerHeights.layer3, horizontalSpacing/2, 3);
      
      for (let i = 0; i < 3; i++) {
        positions[`testing-${i}`] = testingPositions[i];
        positions[`auction-${i}`] = auctionPositions[i];
      }
      
      positions.testing = { x: centerX - horizontalSpacing, y: layerHeights.layer3 };
      positions.auction = { x: centerX + horizontalSpacing, y: layerHeights.layer3 };
      
      // Layer 4: Export and Shipping
      positions.export = { x: centerX - horizontalSpacing/2, y: layerHeights.layer4 };
      positions.shipping = { x: centerX + horizontalSpacing/2, y: layerHeights.layer4 };
      
      // Layer 5: Factory, Spinning, and Risk
      positions.factory = { x: centerX - horizontalSpacing, y: layerHeights.layer5 };
      positions.spinning = { x: centerX, y: layerHeights.layer5 };
      positions.risk = { x: centerX + horizontalSpacing, y: layerHeights.layer5 };
      
      // Additional node types with consistent positioning
      positions.feed = { x: centerX - 2*horizontalSpacing, y: layerHeights.layer2 };
      positions.treatments = { x: centerX + 2*horizontalSpacing, y: layerHeights.layer2 };
      positions.welfare = { x: centerX - 2*horizontalSpacing, y: layerHeights.layer3 };
      positions.biosecurity = { x: centerX + 2*horizontalSpacing, y: layerHeights.layer3 };

      // Safety check - ensure positions is defined
      if (!positions || typeof positions !== 'object') {
        console.error('Positions object is not properly defined');
        return;
      }
      
      // 1. Farm Node - always create a central node
      // Use origin_farm from selectedItem if available
      const farmName = result.FarmName || result.origin_farm || `Farm ${result.PIC || 'Unknown'}`;
      
      // Try to get farm data from the farm_profiles table if available
      let farmData = {};
      
      // Check if we have a PIC to look up farm data
      const farmPIC = result.PIC || result.farm_PIC || (result.raw_data && result.raw_data.PIC);
      
      // Define farm data for the required farms
      const requiredFarms = {
        'NSWSV1357': {
          FarmName: 'Snowy View',
          Region: 'Cooma-Monaro',
          LGA: 'Snowy Monaro',
          OwnerName: 'Snowy View Pastoral Co.',
          SustainaWool: true,
          State: 'NSW'
        },
        'NSWRH2468': {
          FarmName: 'Redbank Hill',
          Region: 'Central West',
          LGA: 'Orange',
          OwnerName: 'Redbank Pastoral Co.',
          SustainaWool: true,
          State: 'NSW'
        },
        'NSWKP1020': {
          FarmName: 'Kookaburra Plains',
          Region: 'New England',
          LGA: 'Armidale',
          OwnerName: 'Ash & Mia Robertson',
          SustainaWool: true,
          State: 'NSW'
        },
        'NSWIB3344': {
          FarmName: 'Ironbark Station',
          Region: 'Central West',
          LGA: 'Dubbo',
          OwnerName: 'Nguyen Family Trust',
          SustainaWool: true,
          State: 'NSW'
        },
        'NSWWC6789': {
          FarmName: 'Wattle Creek',
          Region: 'Central Tablelands',
          LGA: 'Bathurst',
          OwnerName: 'Wattle Creek Family Trust',
          SustainaWool: true,
          State: 'NSW'
        },
        'NSWWD7788': {
          FarmName: 'Waratah Downs',
          Region: 'Monaro',
          LGA: 'Snowy Monaro',
          OwnerName: 'Waratah Pastoral Co.',
          SustainaWool: true,
          State: 'NSW'
        },
        'NSWBR9012': {
          FarmName: 'Bluegum Ridge',
          Region: 'Northern Tablelands',
          LGA: 'Glen Innes',
          OwnerName: 'Bluegum Ridge Pastoral',
          SustainaWool: true,
          State: 'NSW'
        },
        'NSWBV5566': {
          FarmName: 'Banksia Vale',
          Region: 'Riverina',
          LGA: 'Wagga Wagga',
          OwnerName: 'Banksia Vale Pastoral',
          SustainaWool: true,
          State: 'NSW'
        },
        'NSWEP2345': {
          FarmName: 'Eucalyptus Park',
          Region: 'Hunter',
          LGA: 'Muswellbrook',
          OwnerName: 'Eucalyptus Park Pastoral',
          SustainaWool: true,
          State: 'NSW'
        },
        'NSWRP4321': {
          FarmName: 'Rivergum Plains',
          Region: 'Riverina',
          LGA: 'Griffith',
          OwnerName: 'Rivergum Plains Pastoral',
          SustainaWool: true,
          State: 'NSW'
        }
      };
      
      // If we have a known farm, use its data
      if (farmPIC && requiredFarms[farmPIC]) {
        farmData = requiredFarms[farmPIC];
      } else if (result.type === 'farm' && result.name) {
        // Try to match by name if we have a farm type and name
        const farmName = result.name.toLowerCase();
        const matchedFarm = Object.values(requiredFarms).find(farm => 
          farm.FarmName.toLowerCase().includes(farmName) || farmName.includes(farm.FarmName.toLowerCase())
        );
        
        if (matchedFarm) {
          farmData = matchedFarm;
        }
      }
      
      this.nodes.push({
        id: `farm-${baseId}`,
        label: farmName,
        type: 'farm', // Keep this as 'farm' for consistency
        nodeType: 'farm', // Add nodeType for connection matching
        group: 1,
        x: positions.farm.x,
        y: positions.farm.y,
        level: 0, // Mark as a root node
        properties: {
          PIC: farmPIC || result.PIC || 'Unknown',
          FarmName: farmData.FarmName || farmName,
          Region: farmData.Region || result.Region || result.FarmLocation || 'Unknown',
          LGA: farmData.LGA || result.LGA || 'Unknown',
          OwnerName: farmData.OwnerName || result.OwnerName || result.owner || 'Unknown',
          SustainaWool: farmData.SustainaWool ? 'Yes' : (result.SustainaWool ? 'Yes' : 'No'),
          State: farmData.State || result.State || '-'
        }
      });

      // 2. Shearing Node
      if ((result.ShearingCompanyID || result.ShearingTeamLead) && 
          positions.shearing && positions.shearing.x && positions.shearing.y) {
        this.nodes.push({
          id: `shearing-${result.ShearingCompanyID || baseId}`,
          label: result.ShearingTeamLead || `Shearing ${result.ShearingCompanyID}`,
          type: 'shearing',
          group: 2,
          x: positions.shearing.x,
          y: positions.shearing.y,
          properties: {
            shearingCompany: result.ShearingCompanyID,
            teamLead: result.ShearingTeamLead,
            shearingDate: result.ShearingDate,
            sheepCount: result.SheepCount,
            woolWeight: result.WoolWeight,
            classerName: result.ClasserName,
            classerId: result.ClasserID,
            shed: result.Shed || 'Main Shed',
            description: result.Description || 'Merino fleece',
            balesProduced: result.BalesProduced || '2 bales (365kg total)'
          }
        });
      }
      
      // 2.5. NWD Declaration Node
      if (result.LineID && result.MulesingStatus && 
          positions.nwd && positions.nwd.x && positions.nwd.y) {
        this.nodes.push({
          id: `nwd-${result.LineID}`,
          label: `NWD ${result.LineID}`,
          type: 'nwd',
          group: 2.5,
          x: positions.nwd.x,
          y: positions.nwd.y,
          properties: {
            lineId: result.LineID,
            mulesingStatus: result.MulesingStatus,
            painRelief: result.PainRelief,
            dmfr: result.DMFR,
            classerName: result.ClasserName,
            classerId: result.ClasserID
          }
        });
      }
      
      // 2.6. Classer Node
      if ((result.ClasserID || result.ClasserName) && 
          positions.classer_node && positions.classer_node.x && positions.classer_node.y) {
        this.nodes.push({
          id: `classer-${result.ClasserID || baseId}`,
          label: `Classer ${result.ClasserName || result.ClasserID}`,
          type: 'classer_node', // Changed to match the connection type
          nodeType: 'classer', // Add nodeType for consistency
          group: 2.6,
          x: positions.classer_node.x,
          y: positions.classer_node.y,
          level: 1, // Position at layer 1, not as a root node
          properties: {
            classerId: result.ClasserID,
            classerName: result.ClasserName,
            accreditationExpiry: result.AccreditationExpiry,
            businessDetails: result.BusinessDetails
          }
        });
      }

      // 3. Wool Line Node
      if (result.LineID && 
          positions['wool-line'] && positions['wool-line'].x && positions['wool-line'].y) {
        this.nodes.push({
          id: `line-${result.LineID}`,
          label: `Line ${result.LineID}`,
          type: 'wool-line',
          group: 3,
          x: positions['wool-line'].x,
          y: positions['wool-line'].y,
          properties: {
            lineId: result.LineID,
            mulesingStatus: result.MulesingStatus,
            painReliefUsed: result.PainReliefUsed,
            baleWeight: result.BaleWeight,
            paddockSource: result.PaddockSource,
            description: result.Description || result.type || 'Wool line'
          }
        });
      }

      // 4. AWTA Testing Node
      if ((result.TestCertID || result.Micron || result.awta_certificate) && 
          positions.testing && positions.testing.x && positions.testing.y) {
        this.nodes.push({
          id: `test-${result.TestCertID || result.awta_certificate || result.LineID || baseId}`,
          label: `Test ${result.TestCertID || result.awta_certificate || 'Results'}`,
          type: 'testing',
          group: 4,
          x: positions.testing.x,
          y: positions.testing.y,
          properties: {
            testCertId: result.TestCertID || result.awta_certificate,
            micron: result.Micron || result.micron,
            yieldPercent: result.YieldPercent || result.yield,
            stapleLength: result.StapleLength || result.staple_length,
            stapleStrength: result.StapleStrength || result.staple_strength,
            vmContent: result.VMContent || result.vm_content
          }
        });
      }

      // 5. Auction/Broker Node
      if ((result.SaleCenter || result.BrokerName || result.broker) && 
          positions.auction && positions.auction.x && positions.auction.y) {
        this.nodes.push({
          id: `auction-${result.SaleCenter || result.BrokerName || result.broker || baseId}`,
          label: result.SaleCenter || result.BrokerName || result.broker || 'Auction',
          type: 'auction',
          group: 5,
          x: positions.auction.x,
          y: positions.auction.y,
          properties: {
            saleCenter: result.SaleCenter || result.sale_center,
            brokerName: result.BrokerName || result.broker,
            saleDate: result.SaleDate || result.sale_date,
            lotNumber: result.LotNumber || result.lot_number,
            price: result.SalePrice_cents_per_kg_greasy || result.price
          }
        });
      }

      // 6. Factory Node
      if ((result.FactoryName || result.ConsignmentID || result.factory || result.processing_facility) && 
          positions.factory && positions.factory.x && positions.factory.y) {
        this.nodes.push({
          id: `factory-${result.FactoryName || result.factory || result.processing_facility || result.ConsignmentID || baseId}`,
          label: result.FactoryName || result.factory || result.processing_facility || `Factory ${result.ConsignmentID || ''}`,
          type: 'factory',
          group: 6,
          x: positions.factory.x,
          y: positions.factory.y,
          properties: {
            factoryName: result.FactoryName || result.factory || result.processing_facility,
            consignmentId: result.ConsignmentID || result.consignment_id,
            etaFactory: result.ETAFactory || result.eta,
            actualArrival: result.ActualArrival || result.arrival_date,
            delayDays: result.DelayDays || result.delay_days,
            country: result.FactoryCountry || result.country
          }
        });
      }

      // 7. Yarn/Spinning Node
      if ((result.YarnBatchID || result.SpinningMillId || result.yarn_batch || result.spinning_mill) && 
          positions.spinning && positions.spinning.x && positions.spinning.y) {
        this.nodes.push({
          id: `yarn-${result.YarnBatchID || result.yarn_batch || result.SpinningMillId || result.spinning_mill || baseId}`,
          label: `Yarn ${result.YarnBatchID || result.yarn_batch || result.SpinningMillId || result.spinning_mill || ''}`,
          type: 'spinning',
          group: 7,
          x: positions.spinning.x,
          y: positions.spinning.y,
          properties: {
            yarnBatchId: result.YarnBatchID || result.yarn_batch,
            spinningMillId: result.SpinningMillId || result.spinning_mill,
            yarnCount: result.YarnCount || result.yarn_count,
            yarnTwist: result.YarnTwist || result.yarn_twist
          }
        });
      }

      // 8. Fabric Node
      if ((result.FabricRollID || result.fabric_roll || result.fabric_id) && 
          positions.fabric && positions.fabric.x && positions.fabric.y) {
        this.nodes.push({
          id: `fabric-${result.FabricRollID || result.fabric_roll || result.fabric_id || baseId}`,
          label: `Fabric ${result.FabricRollID || result.fabric_roll || result.fabric_id || ''}`,
          type: 'fabric',
          group: 8,
          x: positions.fabric.x,
          y: positions.fabric.y,
          properties: {
            fabricRollId: result.FabricRollID || result.fabric_roll || result.fabric_id,
            fabricWeight: result.FabricWeight || result.fabric_weight || result.gsm,
            fabricType: result.FabricType || result.fabric_type || result.type,
            fabricFinish: result.FabricFinish || result.fabric_finish || result.finish
          }
        });
      }

      // 9. Export/Shipping Node
      if ((result.VesselName || result.ConsignmentID || result.vessel || result.export_id) && 
          positions.export && positions.export.x && positions.export.y) {
        this.nodes.push({
          id: `export-${result.VesselName || result.vessel || result.ConsignmentID || result.export_id || baseId}`,
          label: result.VesselName || result.vessel || `Export ${result.ConsignmentID || result.export_id || ''}`,
          type: 'export',
          group: 9,
          x: positions.export.x,
          y: positions.export.y,
          properties: {
            vesselName: result.VesselName || result.vessel,
            vesselImo: result.VesselIMO || result.vessel_imo,
            vesselFlag: result.VesselFlag || result.vessel_flag,
            freightCompany: result.FreightCompany || result.freight_company,
            portName: result.PortName || result.port,
            portId: result.PortID || result.port_id,
            exportDate: result.ExportDate || result.export_date
          }
        });
      }
      
      // 10. Shipping/Port Node
      if (((result.PortName && result.PortName !== '-') || 
           (result.PortID && result.PortID !== '-') || 
           result.port || 
           result.port_id) && 
          positions.shipping && positions.shipping.x && positions.shipping.y) {
        this.nodes.push({
          id: `shipping-${result.PortID || result.port_id || baseId}`,
          label: result.PortName || result.port || `Port ${result.PortID || result.port_id || ''}`,
          type: 'shipping',
          group: 9.5,
          x: positions.shipping.x,
          y: positions.shipping.y,
          properties: {
            portName: result.PortName || result.port,
            portId: result.PortID || result.port_id,
            portCountry: result.PortCountry || result.port_country,
            portState: result.PortState || result.port_state
          }
        });
      }

      // 11. Risk Assessment Node
      if ((result.RiskLevel || result.risk_level) && 
          positions.risk && positions.risk.x && positions.risk.y) {
        this.nodes.push({
          id: `risk-${result.ConsignmentID || baseId}`,
          label: `Risk: ${result.RiskLevel || result.risk_level}`,
          type: 'risk',
          group: 10,
          x: positions.risk.x,
          y: positions.risk.y,
          properties: {
            riskLevel: result.RiskLevel || result.risk_level,
            riskFactors: result.RiskFactors || result.risk_factors,
            dataQuality: result.DataQuality || result.data_quality,
            lastUpdated: result.LastUpdated || result.last_updated
          }
        });
      }
      
      // 12. Retail/Brand Node
      if ((result.BrandName || result.brand || result.retailer) && 
          positions.retail && positions.retail.x && positions.retail.y) {
        this.nodes.push({
          id: `retail-${result.BrandName || result.brand || result.retailer || baseId}`,
          label: result.BrandName || result.brand || result.retailer || 'Retail',
          type: 'retail',
          group: 11,
          x: positions.retail.x,
          y: positions.retail.y,
          properties: {
            brandName: result.BrandName || result.brand,
            retailer: result.Retailer || result.retailer,
            status: 'ok'
          }
        });
      }
      
      // Only actual data nodes are added - no hard-coded people nodes
    },

    // Helper method to calculate horizontal positions for nodes in a layer
    calculateHorizontalPositions(centerX, y, spacing, count) {
      const positions = [];
      if (count === 1) {
        positions.push({ x: centerX, y });
        return positions;
      }
      
      const totalWidth = spacing * (count - 1);
      const startX = centerX - totalWidth / 2;
      
      for (let i = 0; i < count; i++) {
        positions.push({ x: startX + i * spacing, y });
      }
      
      return positions;
    },
    
    createSupplyChainLinks(result) {
      const baseId = result.PIC || result.LineID || result.ConsignmentID;
      
      // Safety check - if no nodes, don't try to create links
      if (this.nodes.length === 0) return;
      
      const nodeMap = new Map(this.nodes.map(n => [n.id, n]));
      const nodeIds = Array.from(nodeMap.keys());
      
      // Define specific connections for the hierarchical layout with improved structure
      const hierarchicalConnections = [];
      
      // Check if we have connections data from Supabase
      const connections = result.connections || [];
      
      // Determine the root node type (farm or classer)
      const rootNodeType = this.nodes[0].type;
      
      if (rootNodeType === 'farm') {
        // Farm-centric view connections
        // First, add standard hierarchical connections
        hierarchicalConnections.push(
          // Layer 1: Farm to direct connections
          { source: 'farm', targets: ['shearing', 'classer_node'] },
          
          // Layer 2: Shearing/Classer to next layer
          { source: 'shearing', targets: ['nwd', 'wool-line'] },
          { source: 'classer_node', targets: ['wool-line', 'mob'] },
          
          // Layer 3: Wool line connections
          { source: 'wool-line', targets: ['testing', 'auction'] },
          { source: 'nwd', targets: ['testing'] },
          { source: 'mob', targets: ['auction'] },
          
          // Layer 4: Testing/Auction to Export/Shipping
          { source: 'testing', targets: ['export'] },
          { source: 'auction', targets: ['export', 'shipping'] },
          
          // Layer 5: Export/Shipping to Factory/Spinning
          { source: 'export', targets: ['factory', 'risk'] },
          { source: 'shipping', targets: ['factory', 'spinning'] }
        );
        
        // If we have connections data from Supabase, use it to create additional connections
        if (connections && connections.length > 0) {
          // Process each connection from the trace_index
          connections.forEach(conn => {
            // Create connections based on the trace_index relationships
            if (conn.LineID) {
              // Farm to Wool Line
              hierarchicalConnections.push({ source: 'farm', targets: ['wool-line'] });
              
              // If we have test data, connect wool line to testing
              if (conn.TestCertID) {
                hierarchicalConnections.push({ source: 'wool-line', targets: ['testing'] });
              }
              
              // If we have auction data, connect wool line to auction
              if (conn.LotNo) {
                hierarchicalConnections.push({ source: 'wool-line', targets: ['auction'] });
              }
            }
            
            // If we have broker data, connect auction to broker
            if (conn.BrokerCode) {
              hierarchicalConnections.push({ source: 'auction', targets: ['export'] });
            }
            
            // If we have factory data, connect export to factory
            if (conn.FactoryID) {
              hierarchicalConnections.push({ source: 'export', targets: ['factory'] });
            }
            
            // If we have consignment data, connect factory to export
            if (conn.ConsignmentID) {
              hierarchicalConnections.push({ source: 'factory', targets: ['export'] });
            }
            
            // If we have brand data, connect factory to brand
            if (conn.Brand) {
              hierarchicalConnections.push({ source: 'factory', targets: ['retail'] });
            }
          });
        }
      } else if (rootNodeType === 'classer') {
        // Classer-centric view connections
        // Connect classer to farms they work with
        const farmNodes = this.nodes.filter(n => n.type === 'farm' || n.type.startsWith('farm-'));
        farmNodes.forEach(farmNode => {
          hierarchicalConnections.push({ source: 'classer', targets: [farmNode.type] });
        });
        
        // Connect farms to wool lines
        const woolLineNodes = this.nodes.filter(n => n.type === 'wool-line' || n.type.startsWith('wool-line-'));
        farmNodes.forEach(farmNode => {
          const farmIndex = parseInt(farmNode.type.split('-')[1]) || 0;
          const targetWoolLines = woolLineNodes.filter((_, i) => i % farmNodes.length === farmIndex);
          targetWoolLines.forEach(woolLine => {
            hierarchicalConnections.push({ source: farmNode.type, targets: [woolLine.type] });
          });
        });
        
        // Connect wool lines to testing and auction
        woolLineNodes.forEach(woolLine => {
          const woolIndex = parseInt(woolLine.type.split('-')[2]) || 0;
          const testingIndex = woolIndex % 3;
          const auctionIndex = woolIndex % 3;
          hierarchicalConnections.push({ 
            source: woolLine.type, 
            targets: [`testing-${testingIndex}`, `auction-${auctionIndex}`] 
          });
        });
      }
      
      // Create connections based on the hierarchical structure
      hierarchicalConnections.forEach(connection => {
        // Find source node - check both type and nodeType for compatibility
        const sourceNodes = this.nodes.filter(n => n.type === connection.source || n.nodeType === connection.source);
        if (sourceNodes.length === 0) {
          console.log(`No source node found for type: ${connection.source}`);
          return;
        }
        
        const sourceNode = sourceNodes[0];
        
        // Create connections to each target
        connection.targets.forEach(targetType => {
          const targetNodes = this.nodes.filter(n => n.type === targetType || n.nodeType === targetType);
          if (targetNodes.length === 0) {
            console.log(`No target node found for type: ${targetType}`);
            return;
          }
          
          const targetNode = targetNodes[0];
          
          // Add the connection with improved styling
          this.links.push({
            source: sourceNode.id,
            target: targetNode.id,
            type: this.getLinkTypeFromNodes(sourceNode.type, targetNode.type),
            value: 2 // Consistent line weight
          });
          
          console.log(`Created link: ${sourceNode.id} -> ${targetNode.id}`);
        });
      });
      
      
      // Add special links for risk assessment
      if (result.RiskLevel) {
        const riskNodeId = `risk-${result.ConsignmentID || baseId}`;
        const riskNode = nodeMap.get(riskNodeId);
        
        if (riskNode) {
          // Connect risk to export or factory
          const exportNodeId = nodeIds.find(id => id.startsWith('export-'));
          const factoryNodeId = nodeIds.find(id => id.startsWith('factory-'));
          
          if (exportNodeId && nodeMap.get(exportNodeId)) {
            this.links.push({
              source: exportNodeId,
              target: riskNodeId,
              type: 'assessment'
            });
          } else if (factoryNodeId && nodeMap.get(factoryNodeId)) {
            this.links.push({
              source: factoryNodeId,
              target: riskNodeId,
              type: 'assessment'
            });
          }
        }
      }
      
      // Only create connections between actual data nodes - no hard-coded people connections
    },
    
    getLinkTypeFromNodes(sourceType, targetType) {
      const linkTypes = {
        // Farm connections
        'farm_shearing': 'shearing',
        'farm_classer': 'classing',
        'farm_nwd': 'declaration',
        'farm_line': 'production',
        
        // Shearing connections
        'shearing_classer': 'classing',
        'shearing_nwd': 'declaration',
        'shearing_line': 'production',
        
        // Classer connections
        'classer_line': 'classing',
        'classer_nwd': 'declaration',
        
        // NWD connections
        'nwd_line': 'declaration',
        
        // Wool line connections
        'line_testing': 'testing',
        'line_auction': 'sale',
        
        // Testing connections
        'testing_auction': 'certification',
        
        // Auction connections
        'auction_factory': 'processing',
        'auction_export': 'export',
        'auction_spinning': 'processing',
        
        // Factory connections
        'factory_spinning': 'spinning',
        'factory_fabric': 'manufacturing',
        'factory_export': 'export',
        
        // Spinning connections
        'spinning_fabric': 'weaving',
        
        // Export connections
        'export_shipping': 'shipping',
        'export_factory': 'import',
        
        // Shipping connections
        'shipping_factory': 'arrival',
        
        // Fabric connections
        'fabric_retail': 'distribution',
        
        // Risk connections
        'export_risk': 'assessment',
        'factory_risk': 'assessment',
        'shipping_risk': 'assessment'
      };
      
      const key = `${sourceType}_${targetType}`;
      return linkTypes[key] || 'connection';
    },

    updateVisualization() {
      if (!this.svg || this.nodes.length === 0) return;

      // Initialize the simulation for drag functionality
      this.simulation = d3.forceSimulation(this.nodes)
        .force("link", d3.forceLink().id(d => d.id))
        .force("charge", d3.forceManyBody().strength(-50))
        .force("center", d3.forceCenter(400, 600))
        .alphaTarget(0)
        .alphaDecay(0.05)
        .on("tick", () => {}); // Empty tick function as we're using fixed positions
      
      this.svg.selectAll("*").remove();

      // Create a map of nodes by ID for easier link creation
      const nodeMap = {};
      this.nodes.forEach(node => {
        nodeMap[node.id] = node;
      });

      // Process links to ensure they have proper source and target objects
      const processedLinks = this.links.map(link => {
        // If source or target is a string (ID), replace with the actual node object
        const source = typeof link.source === 'string' ? nodeMap[link.source] : link.source;
        const target = typeof link.target === 'string' ? nodeMap[link.target] : link.target;
        
        if (!source || !target) {
          console.error('Missing node for link:', link);
          return null;
        }
        
        return {
          ...link,
          source,
          target
        };
      }).filter(link => link !== null);

      // Add definitions for any gradients or patterns if needed
      const defs = this.svg.append("defs");
      
      // Removed arrowhead marker definition as we no longer need it

      // Create links with improved styling for a cleaner look
      const link = this.svg.append("g")
        .selectAll(".link")
        .data(processedLinks)
        .enter().append("path")
        .attr("class", "link")
        .attr("d", d => {
          // Get source and target positions
          const sourceX = d.source.x;
          const sourceY = d.source.y;
          const targetX = d.target.x;
          const targetY = d.target.y;
          
          // Calculate control points for smooth S-curves
          // This creates more structured, organized connections
          
          // Vertical distance between nodes
          const dy = targetY - sourceY;
          
          // For vertical flow layout, use smooth S-curves
          if (Math.abs(sourceX - targetX) < 10) {
            // Nearly vertical connection - straight line with slight curve
            const midY = sourceY + dy * 0.5;
            return `M${sourceX},${sourceY} C${sourceX},${midY} ${targetX},${midY} ${targetX},${targetY}`;
          } else if (sourceY < targetY) {
            // Downward flow - smooth S-curve
            // Control points at 1/3 and 2/3 of the vertical distance
            const cp1y = sourceY + dy * 0.3;
            const cp2y = targetY - dy * 0.3;
            return `M${sourceX},${sourceY} C${sourceX},${cp1y} ${targetX},${cp2y} ${targetX},${targetY}`;
          } else {
            // Upward or lateral flow - more pronounced curve
            const cp1y = sourceY - Math.abs(dy) * 0.3;
            const cp2y = targetY + Math.abs(dy) * 0.3;
            return `M${sourceX},${sourceY} C${sourceX},${cp1y} ${targetX},${cp2y} ${targetX},${targetY}`;
          }
        })
        .attr("fill", "none")
        .attr("stroke", "#3A895F") // Consistent green color for all connections
        .attr("stroke-width", 2) // Consistent width for cleaner appearance
        .attr("stroke-opacity", 0.7); // Slight transparency for overlapping links
        // Removed arrowheads from all connections

      // Create node groups with consistent styling
      const nodeGroup = this.svg.append("g")
        .selectAll(".node")
        .data(this.nodes)
        .enter().append("g")
        .attr("class", "node")
        .attr("data-type", d => d.type) // Add data-type attribute for CSS targeting
        .attr("transform", d => `translate(${d.x},${d.y})`)
        .call(d3.drag() // Add drag behavior
          .on("start", (event, d) => this.dragstarted(event, d))
          .on("drag", (event, d) => this.dragged(event, d))
          .on("end", (event, d) => this.dragended(event, d))
        );

      // Add different shapes based on node type with consistent green styling
      nodeGroup.each(function(d) {
        const node = d3.select(this);
        const nodeType = d.type;
        
        // Determine if this is a root node
        const isRootNode = nodeType === 'farm' || nodeType === 'classer';
        
        // Determine if this is a commercial entity
        const isCommercialEntity = nodeType === 'testing' || nodeType === 'auction' || 
                                  nodeType === 'export' || nodeType === 'shipping' || 
                                  nodeType === 'factory' || nodeType === 'fabric' || 
                                  nodeType === 'broker';
        
        if (isRootNode) {
          // Root nodes and farms are larger circles
          node.append("circle")
            .attr("r", 25)
            .attr("fill", "#3A895F") // Consistent green for all nodes
            .attr("stroke", "#fff")
            .attr("stroke-width", 3)
            .style("filter", "drop-shadow(0px 2px 4px rgba(0,0,0,0.2))");
        } else if (isCommercialEntity) {
          // Commercial entities are squares
          node.append("rect")
            .attr("width", 40)
            .attr("height", 40)
            .attr("x", -20)
            .attr("y", -20)
            .attr("fill", "#3A895F") // Consistent green for all nodes
            .attr("stroke", "#fff")
            .attr("stroke-width", 2)
            .attr("rx", 4) // Rounded corners
            .attr("ry", 4)
            .style("filter", "drop-shadow(0px 2px 4px rgba(0,0,0,0.2))");
        } else {
          // All other entities are smaller circles
          node.append("circle")
            .attr("r", 15)
            .attr("fill", "#3A895F") // Consistent green for all nodes
            .attr("stroke", "#fff")
            .attr("stroke-width", 2)
            .style("filter", "drop-shadow(0px 2px 4px rgba(0,0,0,0.2))");
        }
        
        // Add status indicator if available (e.g., for compliance status)
        if (d.properties && d.properties.status) {
          node.append("circle")
            .attr("r", 6)
            .attr("cx", 18)
            .attr("cy", -18)
            .attr("fill", d.properties.status === 'ok' ? "#10b981" : "#ef4444")
            .attr("stroke", "#fff")
            .attr("stroke-width", 1.5);
        }
      });
      
      // Add icons inside shapes based on type with improved styling
      nodeGroup.append("text")
        .attr("dy", ".35em")
        .attr("text-anchor", "middle")
        .style("font-size", d => {
          // Determine icon size based on node type
          const nodeType = d.type;
          const isRootNode = nodeType === 'farm' || nodeType === 'classer';
          const isCommercialEntity = nodeType === 'testing' || nodeType === 'auction' || 
                                    nodeType === 'export' || nodeType === 'shipping' || 
                                    nodeType === 'factory' || nodeType === 'fabric';
          
          if (isRootNode) {
            return "16px"; // Larger icons for root nodes
          } else if (isCommercialEntity) {
            return "14px"; // Medium icons for commercial entities
          } else {
            return "12px"; // Smaller icons for other nodes
          }
        })
        .style("font-weight", "bold")
        .style("fill", "#fff")
        .text(d => this.getNodeIcon(d.type));

      // Add node labels below the shapes with improved styling
      nodeGroup.append("text")
        .attr("dy", d => {
          // Position labels based on node size
          const nodeType = d.type;
          const isRootNode = nodeType === 'farm' || nodeType === 'classer';
          const isCommercialEntity = nodeType === 'testing' || nodeType === 'auction' || 
                                    nodeType === 'export' || nodeType === 'shipping' || 
                                    nodeType === 'factory' || nodeType === 'fabric';
          
          if (isRootNode) {
            return 40; // Position for larger nodes
          } else if (isCommercialEntity) {
            return 35; // Position for medium nodes
          } else {
            return 30; // Position for smaller nodes
          }
        })
        .attr("text-anchor", "middle")
        .style("font-size", "12px")
        .style("font-weight", "600")
        .style("fill", "#333")
        .style("font-family", "system-ui, -apple-system, sans-serif")
        .text(d => {
          // Format label with appropriate length
          const label = d.label || d.name || d.type;
          if (label.length > 15) {
            return label.substring(0, 15) + '...';
          }
          return label;
        });
      
      // Add second line of text for commercial entities
      nodeGroup.filter(d => {
        const nodeType = d.type;
        return nodeType === 'testing' || nodeType === 'auction' || nodeType === 'export' || 
               nodeType === 'shipping' || nodeType === 'factory' || nodeType === 'fabric' ||
               nodeType === 'broker';
      }).append("text")
        .attr("dy", 50)
        .attr("text-anchor", "middle")
        .style("font-size", "10px")
        .style("font-weight", "normal")
        .style("fill", "#666")
        .style("font-family", "system-ui, -apple-system, sans-serif")
        .text(d => {
          // Add business type description with improved labels
          const typeLabels = {
            'testing': 'Testing Lab',
            'auction': 'Logistics',
            'export': 'Port',
            'shipping': 'Terminal',
            'factory': 'Processors',
            'fabric': 'Wool Mill',
            'broker': 'Broker',
            'classer': 'Wool Classer'
          };
          return typeLabels[d.type] || d.type;
        });
      
      // No special handling for person nodes as they're not hard-coded anymore

      // Add improved hover effects for better interactivity
      nodeGroup.on('mouseenter', function(event, d) {
        const node = d3.select(this);
        const nodeType = d.type;
        
        // Determine node shape type
        const isRootNode = nodeType === 'farm' || nodeType === 'classer';
        const isCommercialEntity = nodeType === 'testing' || nodeType === 'auction' || 
                                  nodeType === 'export' || nodeType === 'shipping' || 
                                  nodeType === 'factory' || nodeType === 'fabric' || 
                                  nodeType === 'broker';
        
        // Highlight the node with enhanced visual feedback
        if (node.select('circle').size() > 0) {
          // For circle nodes
          const currentRadius = isRootNode ? 25 : 15;
          node.select('circle')
            .transition()
            .duration(200)
            .attr('r', currentRadius + 3)
            .attr('stroke', '#3A895F') // Highlight with same color
            .attr('stroke-width', 4)
            .style('filter', 'drop-shadow(0px 4px 8px rgba(58,137,95,0.4))');
        } else if (node.select('rect').size() > 0) {
          // For rectangle nodes
          node.select('rect')
            .transition()
            .duration(200)
            .attr('width', 46)
            .attr('height', 46)
            .attr('x', -23)
            .attr('y', -23)
            .attr('stroke', '#3A895F') // Highlight with same color
            .attr('stroke-width', 3)
            .style('filter', 'drop-shadow(0px 4px 8px rgba(58,137,95,0.4))');
        }
        
        // Highlight connected links and nodes with improved visual distinction
        const links = d3.selectAll('.link');
        const nodes = d3.selectAll('.node');
        
        // Dim all nodes and links with subtle fade
        links.style('opacity', 0.2);
        nodes.style('opacity', 0.3);
        
        // Highlight this node
        node.style('opacity', 1);
        
        // Find connected links and nodes with improved selection
        const connectedLinks = [];
        links.each(function(l) {
          // Safety check for undefined properties
          if (l && l.source && l.target && d && 
              ((l.source.id && l.source.id === d.id) || 
               (l.target.id && l.target.id === d.id))) {
            connectedLinks.push(l);
            // Enhance connected links
            d3.select(this)
              .style('opacity', 1)
              .style('stroke-width', 3) // Make connected links thicker
              .style('stroke-opacity', 0.9); // More opaque
          }
        });
        
        // Highlight connected nodes with improved visual feedback
        const connectedNodeIds = new Set();
        connectedLinks.forEach(l => {
          connectedNodeIds.add(l.source.id);
          connectedNodeIds.add(l.target.id);
        });
        
        nodes.each(function(n) {
          if (connectedNodeIds.has(n.id)) {
            d3.select(this).style('opacity', 1);
          }
        });
      })
      .on('mouseleave', function(event, d) {
        const node = d3.select(this);
        const nodeType = d.type;
        
        // Determine node shape type
        const isRootNode = nodeType === 'farm' || nodeType === 'classer';
        
        // Reset node size and styling
        if (node.select('circle').size() > 0) {
          // For circle nodes
          const originalRadius = isRootNode ? 25 : 15;
          node.select('circle')
            .transition()
            .duration(200)
            .attr('r', originalRadius)
            .attr('stroke', '#fff')
            .attr('stroke-width', isRootNode ? 3 : 2)
            .style('filter', 'drop-shadow(0px 2px 4px rgba(0,0,0,0.2))');
        } else if (node.select('rect').size() > 0) {
          // For rectangle nodes
          node.select('rect')
            .transition()
            .duration(200)
            .attr('width', 40)
            .attr('height', 40)
            .attr('x', -20)
            .attr('y', -20)
            .attr('stroke', '#fff')
            .attr('stroke-width', 2)
            .style('filter', 'drop-shadow(0px 2px 4px rgba(0,0,0,0.2))');
        }
        
        // Reset all links and nodes opacity with smooth transition
        d3.selectAll('.link')
          .transition()
          .duration(200)
          .style('opacity', 0.7)
          .style('stroke-width', 2)
          .style('stroke-opacity', 0.7);
          
        d3.selectAll('.node')
          .transition()
          .duration(200)
          .style('opacity', 1);
      });

      // Add click handlers for nodes
      nodeGroup.on('click', (event, d) => {
        // Get the SVG container position
        const svgRect = document.getElementById('connections-svg').getBoundingClientRect();
        
        // Calculate modal position relative to the clicked node
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        
        // Calculate position to show modal next to the node
        // Position it to the right of the node by default
        let posX = d.x + svgRect.left + 30; // 30px to the right of node
        let posY = d.y + svgRect.top - 100; // Above the node
        
        // Make sure modal stays within viewport bounds
        const modalWidth = 350; // Approximate modal width
        const modalHeight = 300; // Approximate modal height
        
        // Adjust horizontal position if needed
        if (posX + modalWidth > viewportWidth) {
          // If it would go off right edge, position to the left of the node instead
          posX = d.x + svgRect.left - modalWidth - 30;
        }
        
        // Ensure minimum distance from top
        posY = Math.max(20, posY);
        
        // If it would go off bottom, adjust upward
        if (posY + modalHeight > viewportHeight) {
          posY = viewportHeight - modalHeight - 20;
        }
        
        // Set the modal position
        this.modalPosition = {
          x: posX,
          y: posY
        };
        
        // Set the selected node and show the modal
        this.selectedNode = d;
        this.showNodeDetails = true;
        
        // Prevent event bubbling
        event.stopPropagation();
        
        // Log for debugging
        console.log('Node clicked, modal position:', this.modalPosition);
      });
    },

    getNodeColor(group) {
      const colors = {
        1: '#4CAF50',   // Farm - Green
        2: '#FF9800',   // Shearing - Orange
        3: '#2196F3',   // Wool Line - Blue
        4: '#9C27B0',   // AWTA Test - Purple
        5: '#F44336',   // Auction - Red
        6: '#795548',   // Factory - Brown
        7: '#607D8B',   // Yarn Production - Blue Grey
        8: '#3F51B5',   // Fabric - Indigo
        9: '#009688',   // Export - Teal
        10: '#E91E63'   // Risk Assessment - Pink
      };
      return colors[group] || '#757575';
    },

    getNodeSize(type) {
      const sizes = {
        'Farm': 25,
        'Shearing': 20,
        'Wool Line': 22,
        'AWTA Test': 18,
        'Auction': 20,
        'Factory': 24,
        'Yarn Production': 20,
        'Fabric': 22,
        'Export': 26,
        'Risk Assessment': 16
      };
      return sizes[type] || 20;
    },

    getNodeIcon(type) {
      // Define SVG icons for wool line - this will be rendered as HTML
      if (type === 'wool-line' || type.startsWith('wool-line-')) {
        // Return a custom icon code that will be rendered as text
        // The actual SVG will be added via CSS
        return '📦'; // Using a placeholder that will be styled with CSS
      }
      
      // Use emoji icons for other node types
      const icons = {
        'farm': '🏡',
        'mob': '🐑',
        'feed': '🌾',
        'treatments': '💉',
        'welfare': '🩺',
        'biosecurity': '🛡️',
        'shearing': '✂️',
        'nwd': '📋',
        'classer': '👨‍💼',
        'testing': '🔬',
        'auction': '🏛️',
        'spinning': '🧵',
        'export': '📦',
        'factory': '🏭',
        'fabric': '🧵',
        'shipping': '🚢',
        'retail': '🏪',
        'risk': '⚠️',
        'broker': '🤝'
      };
      return icons[type] || '●';
    },
    
    getNodeSource(type) {
      const sources = {
        'farm': 'Farm profiles',
        'mob': 'Mob register',
        'feed': 'LPA data',
        'treatments': 'LPA data',
        'welfare': 'LPA data',
        'biosecurity': 'Biosecurity logs',
        'shearing': 'Shearing records',
        'nwd': 'NWD declarations',
        'classer': 'Classer register',
        'wool-line': 'Wool line data',
        'testing': 'AWTA results',
        'auction': 'Auction catalog',
        'spinning': 'Factory records',
        'export': 'Export documents',
        'shipping': 'Shipping records',
        'factory': 'Factory inputs',
        'fabric': 'Fabric records',
        'retail': 'Brand records',
        'risk': 'Risk register'
      };
      return sources[type] || 'System data';
    },
    
    closeNodeDetails() {
      this.showNodeDetails = false;
      this.selectedNode = null;
    },
    
    formatPropertyLabel(key) {
      // Convert camelCase to readable labels
      return key
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, str => str.toUpperCase())
        .replace(/Id$/, 'ID')
        .replace(/Pic$/, 'PIC')
        .replace(/Eta$/, 'ETA')
        .replace(/Whp$/, 'WHP')
        .replace(/Qa$/, 'QA');
    },
    
    getCurrentLineId() {
      // First check if we have a selectedItem
      if (this.selectedItem && this.selectedItem.code) {
        return this.selectedItem.code;
      }
      
      // Fall back to searchResult
      if (this.searchResult && this.searchResult.results && this.searchResult.results.length > 0) {
        const selectedResult = this.searchResult.results[this.selectedResultIndex];
        return selectedResult.LineID || selectedResult.code || 'KOO-25-001';
      }
      
      return 'KOO-25-001';
    },

    getLinkColor(type) {
      // Use consistent green styling for all connections
      return '#3A895F'; // Green for all connections
    },

    getLinkWidth(type) {
      // Use consistent width for all connections
      return 2;
    },

    getLinkDash(type) {
      // No dashed lines for consistent styling
      return null;
    },

    formatPropertyName(key) {
      // Special case for PIC to display as P I C
      if (key.toLowerCase() === 'pic') {
        return 'P I C';
      }
      
      // Special case for LGA to display as L G A
      if (key.toLowerCase() === 'lga') {
        return 'L G A';
      }
      
      // Handle other abbreviations that should be spaced out
      const abbreviations = ['id', 'gsm', 'awex', 'nwd', 'eta', 'etd'];
      for (const abbr of abbreviations) {
        if (key.toLowerCase() === abbr) {
          return key.toUpperCase().split('').join(' ');
        }
      }
      
      // Normal case: convert camelCase to Title Case with spaces
      return key.replace(/([A-Z])/g, ' $1')
        .replace(/^./, str => str.toUpperCase())
        .trim();
    },

    // Dynamic data enrichment system
    async enrichTraceData(traceRecord) {
      const enrichedRecord = { ...traceRecord };
      
      try {
        // Process export data with nested lookups
        if (enrichedRecord.ConsignmentID) {
          const { data: exportData, error: exportError } = await this.$supabase
            .from('export_documents')
            .select('VesselName, FreightCompany, PortID, FactoryID')
            .eq('ConsignmentID', enrichedRecord.ConsignmentID)
            .single();

          if (exportData && !exportError) {
            enrichedRecord.VesselName = exportData.VesselName;
            enrichedRecord.FreightCompany = exportData.FreightCompany;
            enrichedRecord.FactoryID = exportData.FactoryID;
            
            // Lookup vessel details
            if (exportData.VesselName) {
              const { data: shipData } = await this.$supabase
                .from('ships')
                .select('IMO, Flag, Operator')
                .eq('VesselName', exportData.VesselName)
                .single();
              
              if (shipData) {
                enrichedRecord.VesselIMO = shipData.IMO;
                enrichedRecord.VesselFlag = shipData.Flag;
                enrichedRecord.VesselOperator = shipData.Operator;
              }
            }
            
            // Lookup factory details
            if (exportData.FactoryID) {
              const { data: factoryData } = await this.$supabase
                .from('factories')
                .select('Name, Country, City, Buyer')
                .eq('FactoryID', exportData.FactoryID)
                .single();
              
              if (factoryData) {
                enrichedRecord.FactoryName = factoryData.Name;
                enrichedRecord.FactoryCountry = factoryData.Country;
                enrichedRecord.FactoryCity = factoryData.City;
                enrichedRecord.FactoryBuyer = factoryData.Buyer;
              }
            }
            
            // Direct port lookup if PortID exists
            if (enrichedRecord.PortID && !enrichedRecord.PortName) {
              const { data: portData } = await this.$supabase
                .from('ports')
                .select('Name, State, OwnerCountry')
                .eq('PortID', enrichedRecord.PortID)
                .single();
              
              if (portData) {
                enrichedRecord.PortName = portData.Name;
                enrichedRecord.PortState = portData.State;
                enrichedRecord.PortCountry = portData.OwnerCountry;
              }
            }
          }
        }
        
        // Lookup fabric details if YarnBatchID exists
        if (enrichedRecord.YarnBatchID) {
          const { data: fabricData } = await this.$supabase
            .from('fabric_records')
            .select('FabricRollID, GSM, FabricType, Finish, FactoryID')
            .eq('YarnBatchID', enrichedRecord.YarnBatchID)
            .limit(1);
          
          if (fabricData && fabricData.length > 0) {
            const fabric = fabricData[0];
            enrichedRecord.FabricRollID = fabric.FabricRollID;
            enrichedRecord.FabricWeight = fabric.GSM;
            enrichedRecord.FabricType = fabric.FabricType;
            enrichedRecord.FabricFinish = fabric.Finish;
            enrichedRecord.SpinningMillId = fabric.FactoryID; // Factory that did the spinning
          }
        }
        
        // Add classer data enrichment
        if (enrichedRecord.ClasserID) {
          const { data: classerData } = await this.$supabase
            .from('classer_register')
            .select('ClasserName, AccreditationExpiry, BusinessDetails')
            .eq('ClasserID', enrichedRecord.ClasserID)
            .limit(1);
          
          if (classerData && classerData.length > 0) {
            const classer = classerData[0];
            enrichedRecord.ClasserName = classer.ClasserName;
            enrichedRecord.AccreditationExpiry = classer.AccreditationExpiry;
            enrichedRecord.BusinessDetails = classer.BusinessDetails;
          }
        }

        // Lookup factory arrival details if ConsignmentID exists
        if (enrichedRecord.ConsignmentID) {
          const { data: factoryInputData } = await this.$supabase
            .from('factory_inputs')
            .select('ArrivalETA_Local, FactoryID')
            .eq('ConsignmentID', enrichedRecord.ConsignmentID)
            .limit(1);
          
          if (factoryInputData && factoryInputData.length > 0) {
            const factoryInput = factoryInputData[0];
            enrichedRecord.ETAFactory = factoryInput.ArrivalETA_Local;
            // Set actual arrival or '-' if no data (future arrival)
            enrichedRecord.ActualArrival = enrichedRecord.ETD_Actual || '-';
            
            // Calculate delay days if we have both ETA and actual arrival
            if (enrichedRecord.ETD_Actual && factoryInput.ArrivalETA_Local) {
              const etaDate = new Date(factoryInput.ArrivalETA_Local);
              const actualDate = new Date(enrichedRecord.ETD_Actual);
              const diffTime = actualDate - etaDate;
              const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
              enrichedRecord.DelayDays = diffDays > 0 ? diffDays : 0;
            } else {
              // Default to 0 delay days when no data available
              enrichedRecord.DelayDays = 0;
            }
          }
        }

        // Set comprehensive default values for missing data to avoid N/A displays
        const defaultValues = {
          // Spinning fields
          SpinningMillId: '-',
          YarnBatchID: '-',
          YarnCount: '-',
          
          // Factory fields
          ETAFactory: '-',
          ActualArrival: '-',
          DelayDays: 0,
          FactoryName: '-',
          ConsignmentID: '-',
          
          // Fabric fields
          FabricWeight: '-',
          FabricType: '-',
          FabricFinish: '-',
          FabricRollID: '-',
          
          // Auction fields
          BrokerName: '-',
          SaleCenter: '-',
          SaleDate: '-',
          LotNumber: '-',
          
          // Export/Shipping fields
          ExportDate: '-',
          ContainerNumber: '-',
          VesselName: '-',
          VesselIMO: '-',
          VesselFlag: '-',
          FreightCompany: '-',
          PortName: '-',
          PortID: '-',
          
          // Mob fields
          Breed: '-',
          MobClass: '-'
        };
        
        // Apply defaults for any missing, null, undefined, or empty values
        Object.keys(defaultValues).forEach(key => {
          if (!enrichedRecord[key] || enrichedRecord[key] === null || enrichedRecord[key] === undefined || enrichedRecord[key] === '') {
            enrichedRecord[key] = defaultValues[key];
          }
        });

        return enrichedRecord;
      } catch (error) {
        console.error('Error enriching trace data:', error);
        return traceRecord;
      }
    },
    
    dragstarted(event, d) {
      // Check if simulation exists before using it
      if (this.simulation && !event.active) {
        this.simulation.alphaTarget(0.3).restart();
      }
      d.fx = d.x;
      d.fy = d.y;
    },

    dragged(event, d) {
      d.fx = event.x;
      d.fy = event.y;
    },

    dragended(event, d) {
      // Check if simulation exists before using it
      if (this.simulation && !event.active) {
        this.simulation.alphaTarget(0);
      }
      d.fx = null;
      d.fy = null;
    }
  }
};
</script>

<style scoped>
/* Connections Graph View - Page Content Only */
.connections-graph-view {
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;
}

/* Custom wool line icon styling */
.wool-line-icon {
  background-image: url('data:image/svg+xml;utf8,<svg width="50" height="50" viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="25" cy="25" r="25" fill="#3A895F"/><mask id="mask0_469_6" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="13" y="13" width="25" height="24"><rect x="13.5" y="13" width="24" height="24" fill="#D9D9D9"/></mask><g mask="url(#mask0_469_6)"><path d="M14 32.2V37H17.6V34.6H24.2V37H27.8V34.6H34.4V37H38V32.2H14Z" fill="white"/><path d="M33.2001 13H18.8001C18.1401 13 17.6001 13.54 17.6001 14.2V28.6C17.6001 29.26 18.1401 29.8 18.8001 29.8H33.2001C33.8601 29.8 34.4001 29.26 34.4001 28.6V14.2C34.4001 13.54 33.8601 13 33.2001 13ZM29.6001 20.2H22.4001V17.8H29.6001V20.2Z" fill="white"/></g></svg>');  
  width: 24px;
  height: 24px;
  display: inline-block;
  background-size: contain;
  background-repeat: no-repeat;
  vertical-align: middle;
}

/* Style for wool line nodes to use the SVG icon */
.node[data-type="wool-line"] text,
.node[data-type^="wool-line-"] text {
  opacity: 0; /* Hide the emoji placeholder */
}

.node[data-type="wool-line"]:before,
.node[data-type^="wool-line-"]:before {
  content: "";
  display: block;
  width: 24px;
  height: 24px;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-image: url('data:image/svg+xml;utf8,<svg width="50" height="50" viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="25" cy="25" r="25" fill="#3A895F"/><mask id="mask0_469_6" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="13" y="13" width="25" height="24"><rect x="13.5" y="13" width="24" height="24" fill="#D9D9D9"/></mask><g mask="url(#mask0_469_6)"><path d="M14 32.2V37H17.6V34.6H24.2V37H27.8V34.6H34.4V37H38V32.2H14Z" fill="white"/><path d="M33.2001 13H18.8001C18.1401 13 17.6001 13.54 17.6001 14.2V28.6C17.6001 29.26 18.1401 29.8 18.8001 29.8H33.2001C33.8601 29.8 34.4001 29.26 34.4001 28.6V14.2C34.4001 13.54 33.8601 13 33.2001 13ZM29.6001 20.2H22.4001V17.8H29.6001V20.2Z" fill="white"/></g></svg>');  
  background-size: contain;
  background-repeat: no-repeat;
  z-index: 2;
}

.graph-header {
  padding: 24px;
  border-bottom: 1px solid #e5e7eb;
  background: white;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 8px;
}


.wool-line-id {
  background: #3A895F;
  color: white;
  padding: 6px 12px;
  border-radius: 6px;
  font-weight: 600;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.wool-line-id .wool-line-icon {
  width: 20px;
  height: 20px;
}

.wool-line-id .line-id-text {
  position: relative;
  top: 1px;
}

.graph-header h3 {
  margin: 0;
  color: #111827;
  font-size: 18px;
}

.text-muted {
  margin: 8px 0 0 0;
  color: #6b7280;
  font-size: 13px;
}

.graph-container {
  flex: 1;
  position: relative;
  overflow: hidden;
  padding: 24px;
}

/* Node Details Modal */
.node-details-modal {
  position: absolute;
  z-index: 1000;
  pointer-events: none; /* Allow clicking through the modal container */
  background: transparent !important; /* Force transparent background */
}

.node-details-card {
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  min-width: 320px;
  max-width: 400px;
  overflow-y: auto;
  max-height: 80vh;
  z-index: 100;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  animation: modal-appear 0.2s ease-out;
  pointer-events: auto; /* Ensure the card itself can receive clicks */
  position: fixed; /* Use fixed positioning for more reliable placement */
}

.node-details-card.right-panel {
  position: fixed;
  top: 0;
  right: 0;
  width: 320px;
  height: 100vh;
  border-radius: 0;
  box-shadow: -4px 0 12px rgba(0, 0, 0, 0.15);
  z-index: 1001;
}

/* Shearing Modal Styles */
.shearing-modal {
  width: 100%;
  height: 100vh;
  overflow-y: auto;
}

.section-header {
  padding: 16px;
  border-bottom: 1px solid #e5e7eb;
}

.section-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #111827;
}

.progress-section {
  padding: 16px;
  border-bottom: 1px solid #e5e7eb;
}

.progress-item {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  gap: 12px;
}

.progress-item:last-child {
  margin-bottom: 0;
}

.progress-label {
  font-size: 14px;
  color: #374151;
  min-width: 80px;
}

.progress-bar {
  flex: 1;
  height: 8px;
  background: #f3f4f6;
  border-radius: 4px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  border-radius: 4px;
  transition: width 0.3s ease;
}

.progress-fill.green {
  background: #10b981;
}

.progress-value {
  font-size: 14px;
  color: #374151;
  min-width: 40px;
  text-align: right;
}

.detail-section {
  padding: 16px;
  border-bottom: 1px solid #e5e7eb;
}

.detail-section:last-child {
  border-bottom: none;
}

.detail-section h4 {
  margin: 0 0 12px 0;
  font-size: 14px;
  font-weight: 600;
  color: #111827;
}

.company-info strong {
  display: block;
  font-size: 14px;
  color: #111827;
  margin-bottom: 8px;
}

.company-details {
  margin-bottom: 12px;
}

.company-details div {
  font-size: 13px;
  color: #374151;
  margin-bottom: 4px;
}

.status-badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.status-badge.green {
  background: #d1fae5;
  color: #065f46;
}

.classer-name {
  font-size: 14px;
  color: #374151;
  margin-bottom: 4px;
}

.classer-name .orange {
  color: #f97316;
  font-weight: 500;
}

.classer-id {
  font-size: 13px;
  color: #6b7280;
  margin-bottom: 12px;
}

.classer-details {
  margin-bottom: 12px;
}

.classer-details div {
  font-size: 13px;
  color: #374151;
  margin-bottom: 4px;
}

.view-certificate-btn {
  background: #f97316;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
}

.view-certificate-btn:hover {
  background: #ea580c;
}

.testing-placeholder {
  height: 40px;
  background: #f9fafb;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  font-size: 13px;
}

.card-header {
  padding: 16px;
  border-bottom: 1px solid #e5e7eb;
  background-color: #f9fafb;
}

.card-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #111827;
}

.card-body {
  padding: 16px;
}

.detail-item {
  margin-bottom: 10px;
  font-size: 14px;
  line-height: 1.5;
  color: #374151;
}

.detail-item strong {
  color: #111827;
  font-weight: 500;
  display: inline-block;
  margin-right: 8px;
}

.card-source {
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #f3f4f6;
  font-size: 12px;
  color: #6b7280;
  font-style: italic;
}

.card-footer {
  padding: 12px 16px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
  background-color: #f9fafb;
}

.details-btn {
  background: none;
  border: none;
  color: #3A895F;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  padding: 6px 12px;
  border-radius: 4px;
  transition: all 0.2s;
}

.details-btn:hover {
  background: rgba(58, 137, 95, 0.1);
}

/* Add animation for modal appearance */
@keyframes modal-appear {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
